import {
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"

import { Permission, Workspace, Connection } from "@/types"
import { usePermissionCheck } from "@/hooks"

export interface PermissionSwitchProps {
  /** The permission required to enable this switch */
  permission: Permission
  /** Workspace to check permissions against */
  workspace?: Workspace
  /** Connection to check permissions against */
  connection?: Connection
  /** Tooltip message to show when user lacks permission */
  disabledTooltip?: string
  /** Switch id for accessibility */
  id?: string
  /** Whether the switch is checked */
  checked?: boolean
  /** Default checked state for uncontrolled usage */
  defaultChecked?: boolean
  /** Callback when checked state changes */
  onCheckedChange?: (checked: boolean) => void
  /** Whether the switch is disabled (in addition to permission check) */
  disabled?: boolean
  /** Whether the switch is required */
  required?: boolean
  /** Switch name for form submission */
  name?: string
  /** Switch value for form submission */
  value?: string
  /** Additional class names */
  className?: string
}

/**
 * A Switch that automatically handles permission checks.
 * When the user lacks the required permission:
 * - The switch is disabled
 * - A tooltip shows the reason (if disabledTooltip is provided)
 */
const PermissionSwitch = ({
  permission,
  workspace,
  connection,
  disabledTooltip,
  disabled,
  id,
  checked,
  defaultChecked,
  onCheckedChange,
  required,
  name,
  value,
  className,
}: PermissionSwitchProps) => {
  const { hasPermission, isLoading } = usePermissionCheck({
    permission,
    workspace,
    connection,
  })

  const isDisabled = disabled || isLoading || !hasPermission

  const switchProps = {
    id,
    checked,
    defaultChecked,
    onCheckedChange,
    required,
    name,
    value,
    className,
  }

  // If disabled due to permission and has tooltip, wrap with Tooltip
  // Use span wrapper to enable tooltip on disabled switch
  if (isDisabled && disabledTooltip && !hasPermission) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex">
            <Switch {...switchProps} disabled />
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    )
  }

  // Normal switch (enabled or disabled without tooltip)
  return <Switch {...switchProps} disabled={isDisabled} />
}

export default PermissionSwitch
